import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, collegeApi, notificationApi, adminApi, ApiResponse } from '@/api/client';
import { API_ENDPOINTS } from '@/api/endpoints';
import { generateSecureToken } from '@/utils/secureToken';
import { encryptField } from '@/utils/payloadEncryption';

// Types matching Flutter models
export interface LoginRequest {
  userName: string;
  password: string;
  refreshToken: string;
  type: 'college';
}

// API returns snake_case field names
export interface LoginResponse {
  access_token: string;
  expires_in: number;
  token_type: string;
  refresh_token: string;
  message?: string;
}

export interface SendOtpRequest {
  email: string;
  phoneNumber: string;
  sendOtpOn: 'email';
}

export interface VerifyOtpRequest {
  userName: string;
  otp: string;
  sendOtpOn: 'email';
  type: 'college';
}

export interface RegisterCollegeRequest {
  collegeName: string;
  collegeEmail: string;
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
  phoneNumber: string;
  password: string;
  isVerified: boolean;
  status: 'Pending';
  collegeUniversityName: string;
  type: 'college';
  profileImage: string;
  isOtherCollege: boolean;
  universityUId: string;
  collegeUId?: string;
  isRegisteredByCandidate: boolean;
}

// API returns mixed case field names for register
export interface RegisterCollegeResponse {
  access_Token: string;
  refresh_token: string | null;
  message: string;
  statusCode?: number;
}

export interface ChangePasswordRequest {
  key: string;
  username: string;
  otp: string;
  passwordResetToken: string;
  newPassword: string;
}

export interface CollegeDetails {
  collegeUId: string;
  collegeName: string;
  collegeEmail: string;
  status: string;
  isVerified: boolean;
  universityName: string;
  // Add more fields as needed
}

// API Response data structure for universities (inner data object)
export interface UniversityApiData {
  universityList: Array<{
    uId: string;
    name: string;
    email?: string;
    website?: string;
  }>;
}

// API Response data structure for colleges (inner data object)
export interface CollegeApiData {
  collegeList: Array<{
    uId: string;
    collegeName: string;
    city?: string;
    zipcode?: string;
  }>;
}

export interface UniversityItem {
  universityUId: string;
  universityName: string;
}

export interface CollegeItem {
  collegeUId: string;
  collegeName: string;
  city: string;
  zipcode: string;
}

// API Security Key - SHA256 hash of 'xyz'
const API_SECURITY_KEY = '3608bca1e44ea6c4d268eb6db02260269892c0b42b86bbf1e77a6fa16c3c9282';

// Common request payload structure
interface CommonRequestPayload {
  page: number;
  pageSize: number;
  totalRecords: number;
  suggestAutocompleteQuery: string;
  filters: Array<{ fieldName: string; fieldValue: string[] }>;
  apiSecurityKey: string;
}

const createCommonPayload = (
  filters: Array<{ fieldName: string; fieldValue: string[] }> = []
): CommonRequestPayload => ({
  page: 1,
  pageSize: 10000,
  totalRecords: 0,
  suggestAutocompleteQuery: '',
  filters,
  apiSecurityKey: API_SECURITY_KEY,
});

// Query keys
export const authKeys = {
  all: ['auth'] as const,
  profile: () => [...authKeys.all, 'profile'] as const,
  collegeProfile: () => [...authKeys.all, 'collegeProfile'] as const,
  coordinatorProfile: () => [...authKeys.all, 'coordinatorProfile'] as const,
  permissions: () => [...authKeys.all, 'permissions'] as const,
  universities: () => [...authKeys.all, 'universities'] as const,
  colleges: (universityUId: string) => [...authKeys.all, 'colleges', universityUId] as const,
  testimonials: () => [...authKeys.all, 'testimonials'] as const,
};

// Login mutation - Uses accounts service
export const useLogin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { userName: string; password: string }) => {
      // Credentials go over the wire encrypted; the backend decrypts both fields
      const request: LoginRequest = {
        userName: encryptField(data.userName.toLowerCase()),
        password: encryptField(data.password),
        refreshToken: '',
        type: 'college',
      };
      const response = await api.account.post<LoginResponse>(API_ENDPOINTS.account.login, request);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.all });
    },
  });
};

// Get college details by UID - Uses college service
export const useGetCollegeDetails = (organizationUId: string | null, enabled = true) => {
  return useQuery({
    queryKey: authKeys.collegeProfile(),
    queryFn: async () => {
      const response = await api.college.post<CollegeDetails>(
        API_ENDPOINTS.college.getCollegeDetailsByUId,
        { uId: organizationUId }
      );
      return response.data;
    },
    enabled: enabled && !!organizationUId,
    staleTime: 10 * 60 * 1000,
  });
};

// Send OTP mutation - Uses accounts service
export const useSendOtp = () => {
  return useMutation({
    mutationFn: async (data: { email: string; phoneNumber?: string; sendOtpOn?: 'email' }) => {
      const request: SendOtpRequest = {
        email: data.email.toLowerCase(),
        phoneNumber: data.phoneNumber || '',
        sendOtpOn: data.sendOtpOn || 'email',
      };
      const response = await api.account.post(API_ENDPOINTS.account.sendOtp, request);
      return response;
    },
  });
};

// Verify OTP mutation - Uses accounts service
export const useVerifyOtp = () => {
  return useMutation({
    mutationFn: async (data: { userName: string; otp: string }) => {
      const request: VerifyOtpRequest = {
        userName: data.userName.toLowerCase(),
        otp: data.otp,
        sendOtpOn: 'email',
        type: 'college',
      };
      const response = await api.account.post(API_ENDPOINTS.account.verifyOtp, request);
      return response;
    },
  });
};

// Register college mutation - Uses college service
// Note: RegisterCollege API returns response directly without wrapping in { statusCode, message, data }
export const useRegisterCollege = () => {
  return useMutation({
    mutationFn: async (data: RegisterCollegeRequest) => {
      // Credentials go over the wire encrypted, same scheme as the login payload
      const request: RegisterCollegeRequest = {
        ...data,
        collegeEmail: encryptField(data.collegeEmail),
        adminEmail: encryptField(data.adminEmail),
        phoneNumber: encryptField(data.phoneNumber),
        password: encryptField(data.password),
      };
      const response = await api.college.post<RegisterCollegeResponse>(
        API_ENDPOINTS.college.registerCollege,
        request
      );
      // API returns { access_Token, message, statusCode } directly, not wrapped in data property
      // response here is already the unwrapped axios data due to api.college.post implementation
      // Check if response has access_Token directly or if it's wrapped
      const result = response as unknown as RegisterCollegeResponse & { data?: RegisterCollegeResponse };
      return result.access_Token ? result : (result.data || result);
    },
  });
};

// Check if mobile and email exist - Uses college service
export const useCheckMobileAndEmail = () => {
  return useMutation({
    mutationFn: async (data: { mobileNumber: string; adminEmail: string; collegeEmail: string }) => {
      const response = await api.college.post(API_ENDPOINTS.college.isMobileAndEmailExists, {
        mobileNumber: data.mobileNumber,
        adminEmail: data.adminEmail.toLowerCase(),
        collegeEmail: data.collegeEmail.toLowerCase(),
        apiSecurityKey: API_SECURITY_KEY,
      });
      return response;
    },
  });
};

// Check if college name exists - Uses college service with secure token
// Returns { data: boolean } - true if college is already registered
export const useCheckCollegeExists = () => {
  return useMutation({
    mutationFn: async (collegeUId: string) => {
      // Generate secure token for authorization (matches Flutter isSecureApi: true)
      const secureToken = generateSecureToken();

      const response = await collegeApi.post(
        API_ENDPOINTS.college.isCollegeNameExists,
        { collegeUId },
        {
          headers: {
            Authorization: secureToken,
          },
        }
      );
      return response.data;
    },
  });
};

// Get all universities - Uses admin service
export const useGetUniversities = (enabled = true) => {
  return useQuery({
    queryKey: authKeys.universities(),
    queryFn: async () => {
      const payload = createCommonPayload();
      const response = await api.admin.post<UniversityApiData>(
        API_ENDPOINTS.admin.getAllUniversities,
        payload
      );
      // api.admin.post returns { statusCode, message, data } - we get the 'data' property
      // response here is already the inner data object due to ApiResponse<T> typing
      const universityList = response.data?.universityList || [];
      return universityList.map((uni) => ({
        universityUId: uni.uId,
        universityName: uni.name,
      }));
    },
    enabled,
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Get colleges by university - Uses admin service (GetAllCollegesInUniversity)
export const useGetCollegesByUniversity = (universityUId: string, enabled = true) => {
  return useQuery({
    queryKey: authKeys.colleges(universityUId),
    queryFn: async () => {
      const payload = createCommonPayload([
        {
          fieldName: 'universityUId',
          fieldValue: [universityUId],
        },
      ]);
      const response = await api.admin.post<CollegeApiData>(
        API_ENDPOINTS.admin.getAllCollegesInUniversity,
        payload
      );
      // response here is already the inner data object due to ApiResponse<T> typing
      const collegeList = response.data?.collegeList || [];
      return collegeList.map((college) => ({
        collegeUId: college.uId,
        collegeName: college.collegeName,
        city: college.city || '',
        zipcode: college.zipcode || '',
      }));
    },
    enabled: enabled && !!universityUId,
    staleTime: 10 * 60 * 1000,
  });
};

// ForgotPasswordRequest request/response types
export interface ForgotPasswordRequest {
  key: string;
  username: string;
  sendOtpOn: 'email';
}

export interface ForgotPasswordResponse {
  otp: string;
  token: string;
}

// Forgot password - Send reset link/OTP - Uses accounts service
export const useForgotPassword = () => {
  return useMutation({
    mutationFn: async (data: { username: string }) => {
      const request: ForgotPasswordRequest = {
        key: API_SECURITY_KEY,
        // Plaintext by design: this endpoint expects an unencrypted username
        username: data.username.toLowerCase(),
        sendOtpOn: 'email',
      };
      const response = await api.account.post<ForgotPasswordResponse>(
        API_ENDPOINTS.account.forgotPasswordRequest,
        request
      );
      return response;
    },
  });
};

// Verify forgot password OTP - Uses accounts service
export const useVerifyForgotPasswordOtp = () => {
  return useMutation({
    mutationFn: async (data: { userName: string; otp: string }) => {
      const response = await api.account.post(API_ENDPOINTS.account.verifyForgotPasswordOtp, {
        // Plaintext by design: this endpoint expects an unencrypted username
        userName: data.userName.toLowerCase(),
        otp: data.otp,
        sendOtpOn: 'email',
      });
      return response;
    },
  });
};

// Change password (reset) - Uses accounts service
// Calls ForgotPasswordCheckOTPAndResetPassword endpoint
export const useChangePassword = () => {
  return useMutation({
    mutationFn: async (data: Omit<ChangePasswordRequest, 'key'>) => {
      const request: ChangePasswordRequest = {
        key: API_SECURITY_KEY,
        username: encryptField(data.username.toLowerCase()),
        otp: data.otp,
        passwordResetToken: data.passwordResetToken,
        newPassword: encryptField(data.newPassword),
      };
      const response = await api.account.post(
        API_ENDPOINTS.account.forgotPasswordCheckOtpAndReset,
        request
      );
      return response;
    },
  });
};

// Verify reCAPTCHA - Uses accounts service
export const useVerifyCaptcha = () => {
  return useMutation({
    mutationFn: async (token: string) => {
      const response = await api.account.post(API_ENDPOINTS.account.captchaSiteVerify, {
        token,
      });
      return response;
    },
  });
};

// Logout mutation
export const useLogout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // No logout endpoint in Flutter, just clear local data
      return true;
    },
    onSuccess: () => {
      queryClient.clear();
    },
  });
};

// SaveDeviceToken request/response types
export interface SaveDeviceTokenRequest {
  deviceId: string;
  deviceToken: string;
  accessToken?: string; // Optional: pass token directly for immediate post-login calls
}

export interface SaveDeviceTokenResponse {
  message: string;
}

// GetCollegeDetailsByUId response types
// API may return different field names depending on the endpoint or context
export interface CollegeDetailsResponse {
  uId: string;
  collegeName: string;
  collegeEmail?: string;
  collegeWebsite?: string;
  collegeDescription?: string;
  collegeHeadquarters?: {
    line1: string;
    line2: string;
    taluka?: string;
    district?: string;
    city: string;
    state: string;
    country: string;
    zipCode: string;
  };
  // API may return address instead of collegeHeadquarters
  address?: {
    line1?: string;
    line2?: string;
    taluka?: string;
    district?: string;
    city?: string;
    state?: string;
    country?: string;
    zipCode?: string;
  };
  // Admin details - API may return in different formats
  adminFirstName?: string;
  adminLastName?: string;
  collegeAdminName?: string; // Alternative: combined name
  designation?: string;
  adminEmail?: {
    email: string;
    isVerified: boolean;
  };
  collegeAdminEmail?: string; // Alternative: direct email string
  adminPhoneNumber?: {
    contactnumber: string;
    isVerified: boolean;
  };
  contactNumber?: string; // Alternative: direct phone string
  profileImage?: string;
  collegeLogoURL?: string;
  collegeLogo?: string;
  collegeRegistrationNumber?: string; // College code
  status?: string; // 'Pending' | 'InReview' | 'Approved'
  universityName?: string;
  collegeUinversityName?: string; // Alternative spelling from API
  universityUId?: string;
  // Placement coordinators - admin profile image might be from here
  collegePlacementCooardinator?: Array<{
    cooardinatorUId?: string;
    cooardinatorName?: string;
    profileImage?: string;
    cooardinatorEmail?: string;
    role?: string;
  }>;
  // Admin profile image might also be returned as adminProfileImage
  adminProfileImage?: string;
}

// Save device token mutation - Uses notification service
export const useSaveDeviceToken = () => {
  return useMutation({
    mutationFn: async (data: SaveDeviceTokenRequest) => {
      const { accessToken, deviceId, deviceToken } = data;

      const requestBody = {
        deviceId,
        deviceToken,
      };


      // Use notificationApi directly to pass custom headers with token
      const response = await notificationApi.post<ApiResponse<SaveDeviceTokenResponse>>(
        API_ENDPOINTS.notification.saveDeviceToken,
        requestBody,
        accessToken ? {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        } : undefined
      );
      return response.data?.data;
    },
  });
};

// Get college details by UId mutation (for post-login flow)
export const useGetCollegeDetailsByUId = () => {
  return useMutation({
    mutationFn: async (params: { uId: string; accessToken?: string }) => {
      const { uId, accessToken } = params;

      // Use collegeApi directly to pass custom headers with token
      const response = await collegeApi.post<ApiResponse<CollegeDetailsResponse>>(
        API_ENDPOINTS.college.getCollegeDetailsByUId,
        {
          uId,
          apiSecurityKey: API_SECURITY_KEY,
        },
        accessToken ? {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        } : undefined
      );
      return response.data?.data;
    },
  });
};

// Generate device ID for web (matches Flutter's getDeviceDetails function)
// Flutter uses: webInfo.vendor + webInfo.userAgent + webInfo.hardwareConcurrency
export const generateDeviceId = (): string => {
 const vendor = navigator.vendor || '';
  const userAgent = navigator.userAgent || '';
  const hardwareConcurrency = navigator.hardwareConcurrency || 0;

  const rawId = `${vendor}${userAgent}${hardwareConcurrency}`;
  return rawId;
};

// Get FCM token for web
// In Flutter, this is stored in localStorage as 'fcmToken' from Firebase Messaging
export const getDeviceToken = (): string => {
  // For web without Firebase setup, return empty string
  // When Firebase is configured, this should return the FCM token
  return localStorage.getItem('fcmToken') || '';
};

// Delete device token request type
export interface DeleteDeviceTokenRequest {
  deviceId: string;
  deviceToken: string;
}

// Delete device token - Called during logout
// This is a direct function (not a hook) so it can be used in AuthProvider logout
export const deleteDeviceToken = async (): Promise<void> => {
  try {
    const deviceId = generateDeviceId();
    const deviceToken = getDeviceToken();

    // Always call API to delete device token
    await notificationApi.post(
      API_ENDPOINTS.notification.deleteDeviceToken,
      { deviceId, deviceToken }
    );
  } catch (error) {
    // Log but don't throw - we still want logout to proceed
    console.warn('Failed to delete device token:', error);
  }
};

// =====================================================
// Document Upload APIs
// =====================================================

export interface UploadDocumentRequest {
  collegeUId: string;
  documentName: string;
  description: string;
  file: File;
}

// Upload college document mutation for onboarding flow
export const useUploadCollegeDocument = () => {
  return useMutation({
    mutationFn: async (data: UploadDocumentRequest) => {
      const formData = new FormData();
      formData.append('CollegeUId', data.collegeUId);
      formData.append('DocumentName', data.documentName);
      formData.append('Description', data.description);
      formData.append('Document', data.file, data.file.name);

      const response = await api.college.upload<{ documentURL: string }>(
        API_ENDPOINTS.college.uploadCollegeDocument,
        formData
      );
      return response.data;
    },
  });
};

// =====================================================
// Dream Companies APIs
// =====================================================

export interface GlobalCompany {
  uId: string;
  companyName?: string;
  name?: string; // API may return 'name' instead of 'companyName'
  companyLogo?: string;
  logo?: string; // API may return 'logo' instead of 'companyLogo'
  industry?: string;
  email?: string;
  phoneNumber?: string;
}

export interface GlobalCompaniesApiData {
  globalCompanyList: GlobalCompany[];
}

// Get all global companies query
export const useGetAllGlobalCompanies = (enabled = true) => {
  return useQuery({
    queryKey: [...authKeys.all, 'globalCompanies'],
    queryFn: async () => {
      const payload = createCommonPayload();
      const response = await api.admin.post<GlobalCompaniesApiData>(
        API_ENDPOINTS.admin.getAllGlobalCompanies,
        payload
      );
      return response.data?.globalCompanyList || [];
    },
    enabled,
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Add dream and past drive companies mutation
// Company item for dream/past drive companies API
export interface DreamCompanyItem {
  uId: string;
  name: string;
  email?: string;
  phoneNumber?: string;
}

export interface AddDreamCompaniesRequest {
  uId?: string;
  collegeUId: string;
  dreamCompanyList: DreamCompanyItem[];
  pastDriveCompanyList: DreamCompanyItem[];
}

export const useAddDreamCompanies = () => {
  return useMutation({
    mutationFn: async (data: AddDreamCompaniesRequest) => {
      const response = await api.admin.post(
        API_ENDPOINTS.admin.addDreamAndPastDriveCompanies,
        {
          ...data,
          apiSecurityKey: API_SECURITY_KEY,
        }
      );
      return response;
    },
  });
};

// =====================================================
// College Verification API
// =====================================================

export interface VerifyCollegeDocumentItem {
  documentName: string;
  description: string;
  documentURL: string;
  documentType: string;
  CollegeUId: string;
  uploadedOn: string;
}

export interface VerifyCollegeSocialMedia {
  platformName: string;
  platformUrl: string;
}

export interface VerifyCollegeAddress {
  line1: string;
  line2: string;
  taluka: string;
  district: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
}

export interface VerifyCollegeRequest {
  uId?: string;
  collegeUId: string;
  collegeName: string;
  verificationType: string;
  docUrl: VerifyCollegeDocumentItem[];
  websiteDomain: string;
  collegeWebsite: string;
  address: VerifyCollegeAddress;
  collegeLogo: string;
  collegeRegistrationNumber: string;
  profileImage: string;
  collegeDescription: string;
  socialMediaModels: VerifyCollegeSocialMedia[];
}

export interface VerifyCollegeResponse {
  message: string;
  statusCode?: number;
}

// Verify college mutation - Called after document upload
export const useVerifyCollege = () => {
  return useMutation({
    mutationFn: async (data: VerifyCollegeRequest) => {
      const response = await api.college.post<VerifyCollegeResponse>(
        API_ENDPOINTS.college.verifyCollege,
        data
      );
      return response;
    },
  });
};

// =====================================================
// Testimonials/First Page Cards API
// =====================================================

export interface TestimonialItem {
  uId: string;
  rating: number;
  title: string;
  displayImage: string;
  personName: string;
  personDesignation: string;
  testimonialType: string;
}

export interface FirstPageCardsApiData {
  page: number;
  pageSize: number;
  totalRecords: number;
  firstPageList: TestimonialItem[];
}

// Get all college testimonials for auth page carousel
export const useGetCollegeTestimonials = (enabled = true) => {
  return useQuery({
    queryKey: authKeys.testimonials(),
    queryFn: async () => {
      const payload = {
        page: 1,
        pageSize: 10000,
        totalRecords: 0,
        suggestAutocompleteQuery: '',
        filters: [
          {
            fieldName: 'testimonialType',
            fieldValue: ['College'],
          },
        ],
        apiSecurityKey: API_SECURITY_KEY,
      };
      const response = await api.admin.post<FirstPageCardsApiData>(
        API_ENDPOINTS.admin.getAllFirstPageCards,
        payload
      );
      return response.data?.firstPageList || [];
    },
    enabled,
    staleTime: 30 * 60 * 1000, // 30 minutes
    retry: false, // Don't retry on failure to prevent blinking
  });
};

// =====================================================
// Platform Stats/Counts API
// =====================================================

export interface PlatformStatsData {
  totalColleges: number;
  totalApprovedColleges: number;
  totalPendingColleges: number;
  totalInReviewColleges: number;
  totalRejectedColleges: number;
  totalCollegesWithoutRejection: number;
  totalCandidates: number;
  totalApprovedCandidates: number;
  totalPendingCandidates: number;
  totalRejectedCandidates: number;
  totalCandidatesWithoutRejection: number;
  totalCompanies: number;
  totalApprovedCompanies: number;
  totalPendingCompanies: number;
  totalInReviewCompanies: number;
  totalRejectedCompanies: number;
  totalCompaniesWithoutRejection: number;
}

// Get platform stats (colleges, candidates, companies count) for auth page
export const useGetPlatformStats = (enabled = true) => {
  return useQuery({
    queryKey: [...authKeys.all, 'platformStats'],
    queryFn: async () => {
      const secureToken = generateSecureToken();
      const payload = {
        year: 0,
        collegeUId: '',
      };
      const response = await adminApi.post<ApiResponse<PlatformStatsData>>(
        API_ENDPOINTS.admin.getAllTotalCounts,
        payload,
        {
          headers: {
            Authorization: secureToken,
          },
        }
      );
      return response.data?.data;
    },
    enabled,
    staleTime: 30 * 60 * 1000, // 30 minutes
    retry: false, // Don't retry on failure to prevent blinking
  });
};

// =====================================================
// Invited College Details API (for college lead pre-fill)
// =====================================================

export interface InvitedCollegeDetails {
  uId?: string;
  collegeName?: string;
  universityName?: string;
  collegeUinversityName?: string; // API may return this spelling
  universityUId?: string;
  collegeUId?: string;
  city?: string;
  zipcode?: string;
  address?: {
    city?: string;
    zipCode?: string;
  };
}

// Get invited college details by UId (for pre-filling registration form)
// Invited college details from Admin API (source=invite)
export interface AdminInvitedCollegeDetails {
  uId?: string;
  collegeName?: string;
  collegeEmail?: string;
  adminEmail?: string;
  adminFirstName?: string;
  adminLastName?: string;
  adminPhoneNumber?: string;
  collegeUniversityName?: string;
}

// Get invited college details from Admin API (for source=invite pre-filling)
export const useGetAdminInvitedCollegeByUId = (uId: string | null, enabled = true) => {
  return useQuery({
    queryKey: [...authKeys.all, 'adminInvitedCollege', uId],
    queryFn: async () => {
      const secureToken = generateSecureToken();
      const response = await adminApi.post<ApiResponse<AdminInvitedCollegeDetails>>(
        API_ENDPOINTS.admin.getInvitedCollegeByUId,
        { uId, apiSecurityKey: API_SECURITY_KEY },
        {
          headers: {
            Authorization: secureToken,
          },
        }
      );
      return response.data?.data;
    },
    enabled: enabled && !!uId,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
};

export const useGetInvitedCollegeByUId = (uId: string | null, enabled = true) => {
  return useQuery({
    queryKey: [...authKeys.all, 'invitedCollege', uId],
    queryFn: async () => {
      const secureToken = generateSecureToken();
      const response = await collegeApi.post<ApiResponse<InvitedCollegeDetails>>(
        API_ENDPOINTS.college.getInvitedCollegeByUId,
        { uId, apiSecurityKey: API_SECURITY_KEY },
        {
          headers: {
            Authorization: secureToken,
          },
        }
      );
      const data = response.data?.data;
      // Normalize the university name field (API returns collegeUinversityName with typo)
      if (data && !data.universityName && data.collegeUinversityName) {
        data.universityName = data.collegeUinversityName;
      }
      // Also set collegeUId from uId if not present
      if (data && !data.collegeUId && data.uId) {
        data.collegeUId = data.uId;
      }
      return data;
    },
    enabled: enabled && !!uId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });
};
